import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmt } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/lib/i18n";
import { PageHeader, Card, Badge, Btn, Modal, elapsed } from "@/components/ui-kit";
import OrderPanel from "@/components/OrderPanel";
import { useRealtime, LiveDot } from "@/hooks/useRealtime";

export default function Orders() {
  const { restaurant } = useAuth();
  const { t } = useT();
  const nav = useNavigate();
  const rid = restaurant.id;
  const [orders, setOrders] = useState([]);
  const [tables, setTables] = useState([]);
  const [open, setOpen] = useState(null);
  const load = useCallback(() => { api.get(`/restaurants/${rid}/orders`).then((r) => setOrders(r.data)); api.get(`/restaurants/${rid}/tables`).then((r) => setTables(r.data)); }, [rid]);
  useEffect(() => { load(); }, [load]);
  const live = useRealtime(rid, load, ["orders", "tables"]);
  const freeTables = tables.filter((x) => !x.order);
  return (
    <div>
      <PageHeader title={t("nav_orders")}>
        <LiveDot connected={live} />
        <select data-testid="new-order-table-select" className="rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold" value="" onChange={(e) => setOpen(tables.find((x) => x.id === e.target.value))}>
          <option value="">+ {t("new_order")}</option>{freeTables.map((x) => <option key={x.id} value={x.id}>{t("table")} {x.name}</option>)}
        </select>
      </PageHeader>
      {orders.length === 0 && <p className="text-slate-500" data-testid="no-orders">{t("no_open_orders")}</p>}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {orders.map((o) => (
          <Card key={o.id} data-testid={`order-card-${o.number}`} className="cursor-pointer transition-all hover:-translate-y-0.5 hover:shadow-md" onClick={() => setOpen(tables.find((x) => x.id === o.table_id))}>
            <div className="flex items-start justify-between">
              <div><p className="font-heading text-xl font-extrabold">{t("table")} {o.table_name}</p><p className="text-xs text-slate-500">#{o.number} · {o.covers} {t("covers_label").toLowerCase()} · {o.waiter}</p></div>
              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-bold">{elapsed(o.created_at)}′</span>
            </div>
            <ul className="mt-3 space-y-1 text-sm">{o.items.filter((i) => !["cancelled", "paid"].includes(i.status)).slice(0, 5).map((i) => <li key={i.id} className="flex justify-between"><span>{i.qty}× {i.name}</span><Badge s={i.status} label={t(`status_${i.status}`)} /></li>)}{o.items.length > 5 && <li className="text-xs text-slate-400">+{o.items.length - 5}</li>}</ul>
            <div className="mt-3 flex items-center justify-between border-t border-slate-100 pt-3">
              <span className="font-heading text-lg font-extrabold">{fmt(o.total)}</span>
              <div className="flex gap-2">{o.bill_requested && <Badge s="ready" label={t("bill_requested")} />}<Btn variant="outline" className="px-3 py-1" onClick={(e) => { e.stopPropagation(); nav(`/app/cashier?order=${o.id}`); }}>{t("pay")}</Btn></div>
            </div>
          </Card>
        ))}
      </div>
      <Modal open={!!open} onClose={() => { setOpen(null); load(); }} title={`${t("table")} ${open?.name}`} wide>
        {open && <OrderPanel table={open} onChanged={load} onClose={() => setOpen(null)} onPay={(o) => nav(`/app/cashier?order=${o.id}`)} />}
      </Modal>
    </div>
  );
}
